import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Paperclip,
  FolderInput,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Ban,
  Cloud,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fmtDate } from "@/lib/jobs";

const STATUS_STYLES = {
  running: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  completed: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  partial: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  failed: "bg-red-500/10 text-red-600 dark:text-red-400",
  cancelled: "bg-muted text-muted-foreground",
};

function fmtBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function AttachmentMigration() {
  const { user } = useAuth();
  const restricted = !!user?.restricted;
  const canWrite = !restricted && user?.can_mutate !== false;

  const [connected, setConnected] = useState(false);
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);

  const [scopeMode, setScopeMode] = useState("org"); // "org" | "object"
  const [selectedObject, setSelectedObject] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const [consentOpen, setConsentOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [starting, setStarting] = useState(false);

  const [current, setCurrent] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const [conns, hist] = await Promise.all([
        api.get("/connections"),
        api.get("/attachments/migrations"),
      ]);
      const sf = (conns.data || []).find((c) => c.system === "salesforce");
      setConnected(!!sf?.connected);
      setHistory(hist.data || []);
      const running = (hist.data || []).find((m) => m.status === "running");
      if (running) setCurrent(running);
      if (sf?.connected) {
        try {
          const objs = await api.get("/extraction/objects", { params: { system: "salesforce" } });
          setObjects(objs.data?.objects || []);
        } catch {
          setObjects([]);
        }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (current && current.status === "running") {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/attachments/migrations/${current.id}`);
          setCurrent(data);
          if (data.status !== "running") {
            clearInterval(pollRef.current);
            setHistory((h) => [data, ...h.filter((m) => m.id !== data.id)]);
            if (data.status === "completed") {
              toast.success(`Converted ${data.converted.toLocaleString()} attachment${data.converted === 1 ? "" : "s"} to Files`);
            } else if (data.status === "partial") {
              toast.warning(`Finished with ${data.failed} error${data.failed === 1 ? "" : "s"} — see the log below`);
            } else if (data.status === "failed") {
              toast.error(data.error || "Migration failed");
            } else if (data.status === "cancelled") {
              toast.info("Migration cancelled");
            }
          }
        } catch {
          // transient — keep polling
        }
      }, 2000);
      return () => clearInterval(pollRef.current);
    }
    return undefined;
  }, [current?.id, current?.status]);

  const runPreview = async () => {
    setPreview(null);
    setPreviewLoading(true);
    try {
      const params = scopeMode === "object" && selectedObject ? { object: selectedObject } : {};
      const { data } = await api.get("/attachments/migration/preview", { params });
      setPreview(data);
      if (data.count === 0) {
        toast.info("No Attachment records found for this scope");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const openConsent = () => {
    setConsentChecked(false);
    setConsentOpen(true);
  };

  const confirmStart = async () => {
    setStarting(true);
    try {
      const body = {
        object: scopeMode === "object" ? selectedObject : null,
        confirm_delete: true,
      };
      const { data } = await api.post("/attachments/migration", body);
      setCurrent(data);
      setConsentOpen(false);
      toast.success("Migration started");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start migration");
    } finally {
      setStarting(false);
    }
  };

  const cancelCurrent = async () => {
    if (!current) return;
    try {
      const { data } = await api.post(`/attachments/migrations/${current.id}/cancel`);
      setCurrent(data);
      toast.info("Cancelling…");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to cancel");
    }
  };

  const scopeLabel = scopeMode === "object" && selectedObject ? selectedObject : "your entire org";
  const busy = !!(current && current.status === "running");
  const progressPct = current && current.total_found
    ? Math.min(100, Math.round(((current.converted + current.skipped + current.failed) / current.total_found) * 100))
    : current && current.status === "running" ? 5 : 0;

  return (
    <div data-testid="attachment-migration-page">
      <div className="mb-8">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Governance</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight lg:text-4xl">Attachments → Files</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Convert classic Salesforce Attachments into Salesforce Files in one click. Each Attachment becomes a
          ContentVersion linked back to the same parent record automatically — nothing to re-wire by hand — and the
          original Attachment is removed once the copy is confirmed.
        </p>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !connected ? (
        <div
          className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border/60 bg-card/40 p-10 text-center"
          data-testid="attachment-migration-empty"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Cloud className="h-6 w-6" />
          </div>
          <p className="mt-4 font-display text-lg font-bold">Connect Salesforce first</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            This action runs against a live, connected Salesforce org.
          </p>
          <Button asChild className="mt-5 font-semibold">
            <Link to="/dashboard/connections">Connect Salesforce</Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-6" data-testid="attachment-migration-scope">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <FolderInput className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="font-display text-base font-bold tracking-tight">Choose scope</p>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Convert every Attachment in the org, or scope this run to one object.
                </p>

                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Select
                    value={scopeMode}
                    onValueChange={(v) => {
                      setScopeMode(v);
                      setPreview(null);
                    }}
                    disabled={busy}
                  >
                    <SelectTrigger className="sm:w-56" data-testid="scope-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="org">Entire org</SelectItem>
                      <SelectItem value="object">Specific object</SelectItem>
                    </SelectContent>
                  </Select>

                  {scopeMode === "object" && (
                    <Select
                      value={selectedObject}
                      onValueChange={(v) => {
                        setSelectedObject(v);
                        setPreview(null);
                      }}
                      disabled={busy}
                    >
                      <SelectTrigger className="sm:w-64" data-testid="scope-object-select">
                        <SelectValue placeholder="Select an object" />
                      </SelectTrigger>
                      <SelectContent>
                        {objects.map((o) => (
                          <SelectItem key={o.name} value={o.name}>
                            {o.label || o.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  <Button
                    variant="outline"
                    onClick={runPreview}
                    disabled={busy || previewLoading || (scopeMode === "object" && !selectedObject)}
                    className="gap-2"
                    data-testid="preview-button"
                  >
                    {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                    Preview
                  </Button>
                </div>

                {preview && (
                  <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4" data-testid="preview-result">
                    <p className="text-sm">
                      Found <span className="font-semibold">{preview.count.toLocaleString()}</span> Attachment
                      {preview.count === 1 ? "" : "s"} ({fmtBytes(preview.total_size_bytes)}) in {scopeLabel}.
                    </p>
                    {preview.warning && (
                      <p className="mt-2 flex items-start gap-2 text-sm text-amber-600 dark:text-amber-400">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        {preview.warning}
                      </p>
                    )}
                    <Button
                      onClick={openConsent}
                      disabled={!canWrite || busy || preview.count === 0}
                      className="mt-4 gap-2 font-semibold"
                      data-testid="start-migration-button"
                    >
                      <FolderInput className="h-4 w-4" />
                      Convert {preview.count.toLocaleString()} attachment{preview.count === 1 ? "" : "s"} to Files
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {current && (
            <div className="rounded-xl border border-border bg-card p-6" data-testid="current-migration">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-display text-base font-bold tracking-tight">
                      {current.scope_label || (current.scope === "org_wide" ? "Entire org" : current.scope)}
                    </p>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${STATUS_STYLES[current.status] || STATUS_STYLES.running}`}>
                      {current.status}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground">Started {fmtDate(current.started_at)}</p>
                </div>
                {busy && (
                  <Button variant="outline" size="sm" onClick={cancelCurrent} className="gap-2 self-start" data-testid="cancel-migration-button">
                    <Ban className="h-4 w-4" /> Cancel
                  </Button>
                )}
              </div>

              <div className="mt-4">
                <Progress value={progressPct} />
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-5">
                  <div><p className="text-muted-foreground">Found</p><p className="font-semibold">{current.total_found?.toLocaleString() ?? "…"}</p></div>
                  <div><p className="text-muted-foreground">Converted</p><p className="font-semibold text-emerald-600 dark:text-emerald-400">{current.converted?.toLocaleString() ?? 0}</p></div>
                  <div><p className="text-muted-foreground">Skipped</p><p className="font-semibold text-amber-600 dark:text-amber-400">{current.skipped?.toLocaleString() ?? 0}</p></div>
                  <div><p className="text-muted-foreground">Failed</p><p className="font-semibold text-red-600 dark:text-red-400">{current.failed?.toLocaleString() ?? 0}</p></div>
                  <div><p className="text-muted-foreground">Deleted originals</p><p className="font-semibold">{current.deleted?.toLocaleString() ?? 0}</p></div>
                </div>
                {current.capped && (
                  <p className="mt-3 text-sm text-amber-600 dark:text-amber-400">
                    Hit the per-run safety cap — run this again to keep converting the rest.
                  </p>
                )}
              </div>

              {(current.errors || []).length > 0 && (
                <div className="mt-4 max-h-64 overflow-y-auto rounded-lg border border-border">
                  <table className="w-full text-left text-sm">
                    <thead className="sticky top-0 bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2">Attachment</th>
                        <th className="px-3 py-2">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {current.errors.map((e, idx) => (
                        <tr key={idx} className="border-t border-border">
                          <td className="px-3 py-2 align-top font-medium">{e.name}</td>
                          <td className="px-3 py-2 align-top text-muted-foreground">{e.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="rounded-xl border border-border bg-card p-6" data-testid="migration-history">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-display text-base font-bold tracking-tight">History</p>
              <Button variant="ghost" size="sm" onClick={load} className="gap-2">
                <RefreshCw className="h-4 w-4" /> Refresh
              </Button>
            </div>
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground">No conversions run yet.</p>
            ) : (
              <div className="space-y-2">
                {history.map((m) => (
                  <div key={m.id} className="flex items-center justify-between rounded-lg border border-border px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      {m.status === "completed" ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      ) : m.status === "failed" ? (
                        <XCircle className="h-4 w-4 text-red-500" />
                      ) : m.status === "cancelled" ? (
                        <Ban className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                      )}
                      <span className="font-medium">{m.scope_label || (m.scope === "org_wide" ? "Entire org" : m.scope)}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${STATUS_STYLES[m.status] || STATUS_STYLES.running}`}>
                        {m.status}
                      </span>
                    </div>
                    <div className="text-muted-foreground">
                      {m.converted.toLocaleString()} converted · {m.failed.toLocaleString()} failed · {fmtDate(m.started_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <AlertDialog open={consentOpen} onOpenChange={(o) => !starting && setConsentOpen(o)}>
        <AlertDialogContent data-testid="consent-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>This permanently deletes the original Attachments</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Every Attachment in {scopeLabel} will be copied into a Salesforce File on the same record
                  (its relation is set automatically), then the original Attachment record is deleted from
                  Salesforce. This cannot be undone from here — the data now lives as a File.
                </p>
                {preview && (
                  <p className="font-medium text-foreground">
                    {preview.count.toLocaleString()} attachment{preview.count === 1 ? "" : "s"} ({fmtBytes(preview.total_size_bytes)})
                  </p>
                )}
                <label className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3">
                  <Checkbox
                    checked={consentChecked}
                    onCheckedChange={(v) => setConsentChecked(!!v)}
                    className="mt-0.5"
                    data-testid="consent-checkbox"
                  />
                  <span className="text-foreground">
                    I understand original Attachments will be permanently deleted once converted, and I want to proceed.
                  </span>
                </label>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setConsentOpen(false)} disabled={starting} data-testid="consent-cancel">
              Cancel
            </Button>
            <Button
              onClick={confirmStart}
              disabled={!consentChecked || starting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="consent-confirm"
            >
              {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Convert & delete originals"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
